// src/pages/Dashboard/Admin/DepensesRecettesDashboard/components/OperationsTable.jsx

import React from "react";
import { FaExternalLinkAlt, FaImage, FaPen, FaTrash } from "react-icons/fa";
import { PiFilePdfFill } from "react-icons/pi";

import { toAbsoluteDepensesRecettesPhotoUrl } from "../../../../../services/depensesrecettesclubPhotoServices";

import {
  fmtEUR,
  formatDateFR,
  getPlanById,
  getPlanLabel,
} from "../utils/accountingDashboardUtils";

function isPdfUrl(url = "") {
  return /\.pdf(\?|$)/i.test(String(url || ""));
}

export default function OperationsTable({
  rows,
  planComptables,
  onEdit,
  onDelete,
}) {
  return (
    <div className="hidden lg:block">
      <div className="overflow-x-auto rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <th className="px-4 py-3 font-bold">Date</th>
              <th className="px-4 py-3 font-bold">Pièce</th>
              <th className="px-4 py-3 font-bold">Désignation</th>
              <th className="px-4 py-3 font-bold">Montant</th>
              <th className="px-4 py-3 font-bold">Bénéficiaire</th>
              <th className="px-4 py-3 font-bold">Catégorie</th>
              <th className="px-4 py-3 font-bold">Type</th>
              <th className="px-4 py-3 font-bold">Règlement</th>
              <th className="px-4 py-3 font-bold">Compte</th>
              <th className="px-4 py-3 font-bold">Lien</th>
              <th className="px-4 py-3 text-right font-bold">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((operation) => {
              const isDepense = operation?.type_operation === "Dépense";
              const plan = getPlanById(
                planComptables,
                operation?.plan_comptable_id
              );
              const hasPdf = isPdfUrl(operation?.photo_url);

              return (
                <tr
                  key={operation.id}
                  className="transition hover:bg-slate-50 dark:hover:bg-slate-900/70"
                >
                  <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-700 dark:text-slate-200">
                    {formatDateFR(operation?.date_operation)}
                  </td>

                  <td className="px-4 py-3">
                    {operation?.photo_url ? (
                      hasPdf ? (
                        <a
                          href={toAbsoluteDepensesRecettesPhotoUrl(
                            operation.photo_url
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-700 ring-1 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                          title="Ouvrir le PDF"
                        >
                          <PiFilePdfFill className="text-xl" />
                        </a>
                      ) : (
                        <img
                          src={toAbsoluteDepensesRecettesPhotoUrl(
                            operation.photo_url
                          )}
                          alt={operation.designation || "Justificatif"}
                          className="h-11 w-11 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                        />
                      )
                    ) : (
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800">
                        <FaImage />
                      </span>
                    )}
                  </td>

                  <td className="min-w-[260px] px-4 py-3">
                    <p className="font-bold text-slate-900 dark:text-white">
                      {operation?.designation || "—"}
                    </p>

                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      Saison {operation?.saison || "—"}
                    </p>
                  </td>

                  <td className="whitespace-nowrap px-4 py-3">
                    <span
                      className={`inline-flex rounded-xl px-2.5 py-1 text-sm font-bold ${
                        isDepense
                          ? "bg-rose-50 text-rose-700 ring-1 ring-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                          : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                      }`}
                    >
                      {fmtEUR(operation?.montant)}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {operation?.beneficiaire || "—"}
                  </td>

                  <td className="px-4 py-3">
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {operation?.categorie || "—"}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold text-white ${
                        isDepense ? "bg-rose-600" : "bg-emerald-600"
                      }`}
                    >
                      {operation?.type_operation || "—"}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {operation?.type_reglement || "—"}
                  </td>

                  <td className="px-4 py-3">
                    {plan ? (
                      <span
                        title={getPlanLabel(
                          planComptables,
                          operation?.plan_comptable_id
                        )}
                        className="cursor-help rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                      >
                        {plan.code}
                      </span>
                    ) : (
                      <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20">
                        À affecter
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {operation?.lien_url ? (
                      <a
                        href={operation.lien_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-700 ring-1 ring-sky-100 transition hover:bg-sky-100 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20"
                        title="Ouvrir le lien"
                      >
                        <FaExternalLinkAlt />
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(operation)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 transition hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                        title="Modifier"
                      >
                        <FaPen />
                      </button>

                      <button
                        type="button"
                        onClick={() => onDelete(operation.id)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-700 ring-1 ring-rose-100 transition hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                        title="Supprimer"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}